// src/utils/imageHelper.js

/**
 * Automatically upgrades low-res or thumbnail CDN URLs from 1mg, Apollo,
 * PharmEasy, etc. to 1000px studio HD packshots, while stripping watermarks.
 */
export const upgradeToHdImageUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    let hdUrl = url;

    // 1. 1mg / Gumlet CDN: strip watermarks and upgrade dimensions to 1000px HD
    if (hdUrl.includes('gumlet.io') || hdUrl.includes('onemg')) {
        hdUrl = hdUrl.replace(/l_watermark_[^,]+,/g, '');
        hdUrl = hdUrl.replace(/w_\d+,h_\d+/g, 'w_1000,h_1000');
        hdUrl = hdUrl.replace(/w_\d+/g, 'w_1000');
        if (!hdUrl.includes('w_1000') && hdUrl.includes('/image/upload/')) {
            hdUrl = hdUrl.replace('/image/upload/', '/image/upload/w_1000,h_1000,c_fit,q_90/');
        }
    }

    // 2. Apollo Pharmacy CDN: upgrade transform parameters
    if (hdUrl.includes('apollo') && hdUrl.includes('?tr=')) {
        hdUrl = hdUrl.replace(/w-\d+/g, 'w-1000').replace(/q-\d+/g, 'q-90');
    }

    // 3. PharmEasy CDN: ensure max dimensions if query params present
    if (hdUrl.includes('pharmeasy.in')) {
        hdUrl = hdUrl.replace(/dim=\d+x\d+/g, 'dim=1000x1000');
    }

    return hdUrl;
};

/**
 * Returns whether a product has a genuine image stored in DB or CDN.
 */
export const hasRealProductImage = (product) => {
    if (!product) return false;
    if (Array.isArray(product.imageUrls) && product.imageUrls.length > 0 && product.imageUrls[0]?.startsWith('http')) {
        return true;
    }
    const img = product.imageUrl || product.image;
    if (img && typeof img === 'string') {
        if (img.startsWith('http') && !img.includes('/placeholders/') && !img.includes('placehold.co')) {
            return true;
        }
        if (!img.startsWith('http') && img.includes('.')) {
            return true;
        }
    }
    return false;
};

export const getPlaceholderImage = (productName = '') => {
    const lowerName = productName.toLowerCase();
    
    if (lowerName.includes('syp') || lowerName.includes('syrup') || lowerName.includes('susp')) {
        return '/images/placeholders/generic_syrup.png';
    }
    if (lowerName.includes('inj') || lowerName.includes('vial') || lowerName.includes('amp')) {
        return '/images/placeholders/generic_injection.png';
    }
    if (lowerName.includes('cream') || lowerName.includes('oint') || lowerName.includes('gel')) {
        return '/images/placeholders/generic_cream.png';
    }
    if (lowerName.includes('drop') || lowerName.includes('eye drop') || lowerName.includes('ear drop')) {
        return '/images/placeholders/generic_drops.png';
    }
    if (lowerName.includes('cap') || lowerName.includes('capsule')) {
        return '/images/placeholders/generic_capsule.png';
    }
    
    // Default to tablet if 'tab' or 'tablet' is present, or as a general fallback
    return '/images/placeholders/generic_tablet.png';
};

export const getCleanImageUrl = (product, productName) => {
    let resolvedUrl = null;

    // 1. Always prioritize newly scraped, non-watermarked AI images
    if (product?.imageUrls && product.imageUrls.length > 0 && product.imageUrls[0]) {
        resolvedUrl = product.imageUrls[0];
    }
    // 2. If imageUrl exists and is a valid HTTP link, use it
    else if (product?.imageUrl && product.imageUrl.startsWith('http')) {
        resolvedUrl = product.imageUrl;
    }
    else if (product?.image && product.image.startsWith('http')) {
        resolvedUrl = product.image;
    }
    // 3. If it's a local filename (e.g. "BetnovateC_Cream.jpg") from the database
    // generate the correct Firebase Storage URL
    else {
        let localImage = product?.imageUrl || product?.image || product?.imageFilename;
        if (localImage && !localImage.startsWith('http') && localImage.includes('.')) {
            const encodedFilename = encodeURIComponent(localImage);
            resolvedUrl = `https://firebasestorage.googleapis.com/v0/b/pharma-retail-online.firebasestorage.app/o/images%2F${encodedFilename}?alt=media`;
        }
    }

    if (resolvedUrl) {
        return upgradeToHdImageUrl(resolvedUrl);
    }

    // 4. Fallback to generic placeholder
    return getPlaceholderImage(productName);
};
